(() => {
  const clamp = (value, min = 0, max = 1) => Math.max(min, Math.min(max, value));
  const smooth = (value) => {
    const t = clamp(value);
    return t * t * (3 - 2 * t);
  };

  function initialiseTheme() {
    if (window.toadThemeAbort) window.toadThemeAbort.abort();
    const controller = new AbortController();
    window.toadThemeAbort = controller;
    const { signal } = controller;
    const body = document.body;
    const menuButton = document.querySelector('.rail-menu-button');
    const menu = document.querySelector('.menu-drawer');
    const backdrop = document.querySelector('.menu-backdrop');

    const setMenu = (open) => {
      body.classList.toggle('menu-open', open);
      menuButton?.classList.toggle('is-open', open);
      menu?.classList.toggle('is-open', open);
      backdrop?.classList.toggle('is-open', open);
      menuButton?.setAttribute('aria-expanded', String(open));
      menuButton?.setAttribute('aria-label', open ? 'Close menu' : 'Open menu');
      if (backdrop) backdrop.tabIndex = open ? 0 : -1;
    };

    menuButton?.addEventListener('click', () => setMenu(!body.classList.contains('menu-open')), { signal });
    backdrop?.addEventListener('click', () => setMenu(false), { signal });
    menu?.querySelectorAll('a').forEach((link) => link.addEventListener('click', () => setMenu(false), { signal }));
    window.addEventListener('keydown', (event) => { if (event.key === 'Escape') setMenu(false); }, { signal });

    const sections = [...document.querySelectorAll('[data-motion]')];
    const revealItems = document.querySelectorAll('[data-reveal]');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const shopSection = document.querySelector('.u-shop');
    const shopProducts = [...(shopSection?.querySelectorAll('.shop-object') || [])];
    const shopLabels = [...(shopSection?.querySelectorAll('.shop-label') || [])];
    let frame = 0;
    let shopFrame = 0;
    let shopCurrent = 0;
    let shopTarget = 0;
    let shopReady = false;

    const renderShop = (progress) => {
      if (!shopSection || !shopProducts.length) return;
      shopSection.style.setProperty('--shop-p', progress.toFixed(4));
      const phase = progress * Math.max(1, shopProducts.length - 1);
      shopProducts.forEach((product, index) => {
        const relative = index - phase;
        const distance = Math.abs(relative);
        const opacity = smooth(clamp(1 - distance / 1.05));
        const offset = relative * 124;
        const scale = 0.82 + opacity * 0.18;
        product.style.setProperty('--object-opacity', opacity.toFixed(3));
        product.style.setProperty('--object-y', `${offset.toFixed(2)}px`);
        product.style.setProperty('--object-scale', scale.toFixed(3));
        product.style.zIndex = String(5 + Math.round(opacity * 10));
      });
      shopLabels.forEach((label, index) => {
        const opacity = smooth(clamp(1 - Math.abs(index - phase) / 0.72));
        label.style.setProperty('--label-opacity', opacity.toFixed(3));
        label.style.setProperty('--label-y', `${((1 - opacity) * 7).toFixed(2)}px`);
      });
    };

    const animateShop = () => {
      const difference = shopTarget - shopCurrent;
      /* Keep the inertial turntable feel without allowing the artwork to trail
         several seconds behind a fast Shopify/mobile scroll gesture. */
      const easing = Math.abs(difference) > 0.32 ? 0.34 : 0.24;
      shopCurrent += difference * easing;
      if (Math.abs(difference) < 0.0005) shopCurrent = shopTarget;
      renderShop(shopCurrent);
      if (shopCurrent !== shopTarget) shopFrame = requestAnimationFrame(animateShop);
      else shopFrame = 0;
    };

    const revealObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          revealObserver.unobserve(entry.target);
        }
      });
    }, { threshold: 0.14 });
    revealItems.forEach((item) => revealObserver.observe(item));
    signal.addEventListener('abort', () => revealObserver.disconnect(), { once: true });

    const update = () => {
      const viewport = window.innerHeight;
      const pageRange = Math.max(1, document.documentElement.scrollHeight - viewport);
      document.documentElement.style.setProperty('--page-progress', String(clamp(window.scrollY / pageRange)));
      let nearestId = 'top';
      let nearestDistance = Number.POSITIVE_INFINITY;

      sections.forEach((section) => {
        const rect = section.getBoundingClientRect();
        const id = section.id || 'top';
        const distance = Math.abs(rect.top - viewport * 0.34);
        if (rect.bottom > viewport * 0.18 && rect.top < viewport * 0.82 && distance < nearestDistance) {
          nearestDistance = distance;
          nearestId = id;
        }
        if (reducedMotion.matches) return;

        let passage = clamp((viewport - rect.top) / (viewport + rect.height));
        const driver = document.querySelector(`[data-scroll-driver="${id}"]`);
        if (driver) {
          const driverRect = driver.getBoundingClientRect();
          passage = clamp((viewport - driverRect.top) / Math.max(1, driverRect.height));
        }
        section.style.setProperty('--passage', passage.toFixed(4));

        if (section.classList.contains('u-hero')) section.style.setProperty('--hero-p', clamp(-rect.top / Math.max(1, rect.height)).toFixed(4));
        if (section.classList.contains('u-garden')) section.style.setProperty('--garden-p', smooth(clamp(-rect.top / Math.max(1, rect.height - viewport))).toFixed(4));
        if (section.classList.contains('u-food')) section.style.setProperty('--food-p', smooth(passage).toFixed(4));
        if (section.classList.contains('u-shop')) {
          shopTarget = passage;
          if (!shopReady) {
            shopCurrent = passage;
            shopReady = true;
            renderShop(passage);
          } else if (!shopFrame) shopFrame = requestAnimationFrame(animateShop);
        }

        const cells = section.classList.contains('u-history')
          ? section.querySelectorAll('.history-image-grid i')
          : section.querySelectorAll('.motion-grid i');
        if (cells.length) {
          const activeCell = Math.min(cells.length - 1, Math.floor(passage * cells.length * 1.35));
          cells.forEach((cell, index) => {
            cell.classList.toggle('is-lit', index === activeCell || index === activeCell + 4);
            cell.classList.toggle('is-gone', index < activeCell - 3);
          });
        }
      });

      document.querySelectorAll('.menu-links a').forEach((link) => {
        const href = link.getAttribute('href') || '';
        link.classList.toggle('is-current', href.endsWith(`#${nearestId}`));
      });
      frame = 0;
    };

    const requestUpdate = () => { if (!frame) frame = requestAnimationFrame(update); };
    update();
    window.addEventListener('scroll', requestUpdate, { passive: true, signal });
    window.addEventListener('resize', requestUpdate, { signal });
    signal.addEventListener('abort', () => {
      if (frame) cancelAnimationFrame(frame);
      if (shopFrame) cancelAnimationFrame(shopFrame);
    }, { once: true });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialiseTheme, { once: true });
  else initialiseTheme();
  document.addEventListener('shopify:section:load', initialiseTheme);
})();
